import type { AuthenticationResult } from '../types/auth';

export const SESSION_EXPIRED_EVENT = 'conolandia:session-expired';
// Fase 25A: la cuenta (business) esta SUSPENDED/ARCHIVED. El backend responde 403 con estos codigos
// en toda ruta operacional; el shell muestra una pantalla neutral con opcion de cerrar sesion.
export const ACCOUNT_BLOCKED_EVENT = 'conolandia:account-blocked';
export const SESSION_REFRESHED_EVENT = 'conolandia:session-refreshed';
// ONB-007: "vuelve a leer el estado comercial". Lo dispara el cliente cuando el servidor niega una
// escritura por SOLO LECTURA comercial (trial, gracia o plan terminados) y la pantalla de facturación
// cuando algo cambió. A diferencia de una cuenta bloqueada, la app sigue abierta (se puede consultar
// todo): el shell solo actualiza su aviso y su menú.
export const COMMERCIAL_STATUS_EVENT = 'conolandia:commercial-status';

const API_BASE_URL = (
  import.meta.env.VITE_API_URL || 'http://localhost:3000'
).replace(/\/$/, '');

interface ApiRequestOptions extends RequestInit {
  auth?: boolean;
}

interface ErrorPayload {
  message?: string;
  error?: string;
  details?: unknown;
  retryAfterSeconds?: number;
}

let refreshPromise: Promise<boolean> | null = null;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly payload?: ErrorPayload,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { auth = true, ...requestOptions } = options;
  let response = await sendRequest(path, requestOptions);

  if (response.status === 401 && auth && path !== '/api/auth/refresh') {
    const refreshed = await refreshCookieSession();

    if (refreshed) {
      response = await sendRequest(path, requestOptions);
    } else {
      notifySessionExpired();
    }
  }

  const payload = await readResponsePayload(response);

  if (!response.ok) {
    const errorPayload = isErrorPayload(payload) ? payload : undefined;

    if (response.status === 401 && auth) {
      notifySessionExpired();
    }

    // Fase 25A: cuenta bloqueada por lifecycle SaaS (suspendida/archivada) => aviso global.
    if (response.status === 403 && (errorPayload?.error === 'BUSINESS_SUSPENDED' || errorPayload?.error === 'BUSINESS_ARCHIVED')) {
      window.dispatchEvent(new CustomEvent<string>(ACCOUNT_BLOCKED_EVENT, { detail: errorPayload.error }));
    }

    // ONB-007: solo lectura comercial => aviso global (sin sacar a la persona de la pantalla).
    if (response.status === 403 && errorPayload?.error === 'SAAS_READ_ONLY') {
      window.dispatchEvent(new CustomEvent(COMMERCIAL_STATUS_EVENT));
    }

    throw new ApiError(
      response.status,
      getApiErrorMessage(response.status, errorPayload),
      errorPayload,
    );
  }

  return payload as T;
}

export function getErrorMessage(
  error: unknown,
  fallback = 'Ocurrió un error inesperado.',
): string {
  if (!(error instanceof Error)) return fallback;
  if (/failed to fetch|networkerror|load failed/i.test(error.message)) return 'No fue posible conectar con el servidor.';
  if (/unauthorized/i.test(error.message)) return 'Tu sesión expiró. Vuelve a iniciar sesión.';
  if (/forbidden/i.test(error.message)) return 'No tienes permiso para realizar esta acción.';
  if (/not found/i.test(error.message)) return 'No se encontró el registro solicitado.';
  return error.message || fallback;
}

async function sendRequest(
  path: string,
  requestOptions: RequestInit,
): Promise<Response> {
  const headers = new Headers(requestOptions.headers);
  headers.set('Accept', 'application/json');

  if (requestOptions.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  try {
    return await fetch(`${API_BASE_URL}${path}`, {
      ...requestOptions,
      credentials: 'include',
      headers,
    });
  } catch {
    throw new ApiError(
      0,
      'No fue posible conectar con el servidor. Verifica que el backend esté activo.',
    );
  }
}

async function refreshCookieSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = performSessionRefresh().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

async function performSessionRefresh(): Promise<boolean> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
  } catch {
    return false;
  }

  if (!response.ok) return false;

  const payload = await readResponsePayload(response);

  if (!isAuthenticationResult(payload)) return false;

  window.dispatchEvent(new CustomEvent<AuthenticationResult>(
    SESSION_REFRESHED_EVENT,
    { detail: payload },
  ));

  return true;
}

function notifySessionExpired(): void {
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

async function readResponsePayload(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) return undefined;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isErrorPayload(payload: unknown): payload is ErrorPayload {
  return typeof payload === 'object' && payload !== null;
}

function isAuthenticationResult(payload: unknown): payload is AuthenticationResult {
  if (typeof payload !== 'object' || payload === null) return false;

  const result = payload as Partial<AuthenticationResult>;
  return (
    typeof result.user === 'object' &&
    result.user !== null &&
    Array.isArray(result.stores) &&
    typeof result.accessTokenExpiresAt === 'string' &&
    typeof result.sessionExpiresAt === 'string'
  );
}

/**
 * Exportada para probarla (Fase A de auditoría, N360-26). La regla se mantiene: solo se muestra el
 * texto del backend en los casos enumerados arriba; cualquier código sin traducción cae al genérico
 * del estado HTTP, nunca al `message` crudo.
 */
export function getApiErrorMessage(status: number, payload?: ErrorPayload): string {
  if (payload?.error === 'LOGIN_TEMPORARILY_LOCKED') {
    const minutes = Math.max(1, Math.ceil((payload.retryAfterSeconds ?? 60) / 60));
    return `Demasiados intentos de acceso. Intenta nuevamente en ${minutes} minuto${minutes === 1 ? '' : 's'}.`;
  }

  // El backend arma este mensaje con los NOMBRES de los elementos bloqueantes (proveedor/insumo y su
  // estado); es más útil que cualquier texto fijo, así que se muestra tal cual cuando viene.
  if (payload?.error === 'PURCHASE_CATALOG_PENDING' && payload.message) {
    return payload.message;
  }

  // Mismo criterio: el backend detalla QUÉ bloqueó el descuento (producto sin receta, insumo sin
  // stock, unidad incompatible...) al re-validar dentro de la transacción; el texto genérico
  // escondía la causa cuando el auto-sync mete tickets nuevos entre simular y confirmar.
  if (payload?.error === 'SALES_POSTING_BLOCKED' && payload.message) {
    return `${payload.message} Vuelve a calcular la simulación e inténtalo de nuevo.`;
  }

  // F1: el backend distingue al dueño (sin tiendas activas) de quien no tiene tienda asignada; el
  // texto fijo de abajo queda solo como respaldo.
  if (payload?.error === 'WASTE_STORE_NOT_ASSIGNED' && payload.message) {
    return payload.message;
  }

  // F3: los mensajes del conteo los arma el backend con el caso concreto (qué ventas faltan, la
  // existencia nueva, por qué se pide motivo); un texto fijo escondería qué hacer.
  if (payload?.error?.startsWith('COUNT_') && payload.message) {
    return payload.message;
  }

  // F4: el rechazo de Ventas Otros por existencias dice QUÉ insumo falta (al EMPLOYEE sin cantidades, lo
  // decide el backend); el texto genérico de 409 no decía qué hacer.
  if (payload?.error === 'OTHER_SALE_CONSUMPTION_INSUFFICIENT_STOCK' && payload.message) {
    return payload.message;
  }

  // F2: las solicitudes dicen el caso concreto (solo el mismo día, se contó después, quedó rechazada
  // sola...); el genérico escondía el porqué. EXCEPTO VOID_REQUEST_APPLY_FAILED: ese envuelve cualquier
  // error interno al aplicar la anulación y no debe llegar crudo a la pantalla (tiene su texto fijo).
  if (payload?.error?.startsWith('VOID_REQUEST_') && payload.error !== 'VOID_REQUEST_APPLY_FAILED' && payload.message) {
    return payload.message;
  }

  if (payload?.error && ERROR_MESSAGES[payload.error]) {
    return ERROR_MESSAGES[payload.error];
  }

  return getStatusMessage(status);
}

const ERROR_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: 'La información enviada no es válida. Revisa los campos.',
  INVALID_CREDENTIALS: 'El usuario o la contraseña son incorrectos.',
  USER_INACTIVE: 'Este usuario está desactivado. Contacta al administrador.',
  AUTHORIZATION_REQUIRED: 'Tu sesión expiró. Inicia sesión nuevamente.',
  ACCESS_TOKEN_INVALID: 'Tu sesión expiró. Inicia sesión nuevamente.',
  REFRESH_TOKEN_INVALID: 'Tu sesión expiró. Inicia sesión nuevamente.',
  SESSION_INVALID: 'Tu sesión ya no es válida. Inicia sesión nuevamente.',
  BUSINESS_INACTIVE: 'No tienes acceso a este negocio.',
  TENANT_REQUIRED: 'No tienes acceso a este negocio.',
  // Fase 25A: SaaS.
  BUSINESS_SUSPENDED: 'Esta cuenta está suspendida.',
  BUSINESS_ARCHIVED: 'Esta cuenta está archivada.',
  BUSINESS_PLAN_MISSING: 'La cuenta no tiene un plan válido asignado. Contacta al administrador.',
  STORE_LIMIT_REACHED: 'Alcanzaste el límite de tiendas de tu plan.',
  USER_LIMIT_REACHED: 'Alcanzaste el límite de usuarios de tu plan.',
  FEATURE_NOT_AVAILABLE: 'Esta función no está disponible en tu plan.',
  // ONB-007: facturación SaaS (trial, plan y solo lectura comercial). Sin lenguaje de castigo.
  SAAS_READ_ONLY: 'Tu información está aquí. Para seguir registrando movimientos, activa un plan.',
  SAAS_BILLING_DISABLED: 'La facturación no está disponible por ahora.',
  BILLING_OWNER_ONLY: 'Solo la persona dueña del negocio puede administrar el plan y la facturación.',
  BILLING_VIEW_FORBIDDEN: 'Tu rol no puede ver el plan del negocio.',
  BILLING_NOT_APPLICABLE: 'El plan de este negocio lo administra Negocio360.',
  BILLING_PLAN_NOT_AVAILABLE: 'Ese plan ya no está disponible. Actualiza la página.',
  BILLING_SUBSCRIPTION_EXISTS: 'Tu negocio ya tiene un plan contratado.',
  BILLING_CHECKOUT_IN_PROGRESS: 'Estamos confirmando tu forma de pago. Intenta de nuevo en un momento.',
  BILLING_NO_SUBSCRIPTION: 'Tu negocio todavía no tiene un plan contratado.',
  BILLING_NO_PAYMENT_PROFILE: 'Todavía no hay una forma de pago registrada.',
  BILLING_NOT_CANCELABLE: 'Este plan no se puede cancelar en este momento.',
  BILLING_NOT_RESUMABLE: 'Este plan no tiene una cancelación que se pueda revertir.',
  BILLING_PROVIDER_UNAVAILABLE: 'No pudimos comunicarnos con el servicio de pagos. Intenta de nuevo en unos minutos.',
  BILLING_CONFLICT: 'Otro cambio se aplicó al mismo tiempo. Intenta de nuevo.',
  BILLING_RATE_LIMITED: 'Demasiados intentos seguidos. Espera un minuto e intenta de nuevo.',
  REGISTRATION_CONFLICT: 'Este registro ya está en proceso. Intenta de nuevo en un momento.',
  BUSINESS_OR_USER_ALREADY_EXISTS: 'El negocio, email o usuario ya está registrado.',
  ACTIVE_OWNER_EXISTS: 'Ya existe un usuario Dueño activo.',
  USER_ALREADY_EXISTS: 'El email o username ya está registrado.',
  OWNER_PROTECTED: 'El usuario Dueño no puede modificarse desde este flujo.',
  MANAGER_PEER_PROTECTED: 'No puedes modificar a un usuario con este rol.',
  ADMIN_PERMISSION_REQUIRED: 'No tienes permiso para administrar usuarios.',
  OWNER_PERMISSION_REQUIRED: 'Solo el Dueño puede realizar esta acción.',
  ORIGIN_NOT_ALLOWED: 'El origen de esta solicitud no está autorizado.',
  HTTPS_REQUIRED: 'La conexión segura HTTPS es obligatoria.',
  STORE_REQUIRED: 'El usuario necesita al menos una tienda activa.',
  INVALID_STORES: 'Una o más tiendas seleccionadas no son válidas o están inactivas.',
  USER_NOT_FOUND: 'El usuario solicitado no existe.',
  STORE_NOT_FOUND: 'La tienda solicitada no existe o no está asignada a tu cuenta.',
  STORE_EXTERNAL_ID_EXISTS: 'El identificador de integración de la tienda ya está registrado.',
  PURCHASE_STORE_REQUIRED: 'Selecciona una tienda para registrar la entrada de stock.',
  PURCHASE_LIST_STORE_REQUIRED: 'Selecciona una tienda para consultar compras.',
  PURCHASE_STORE_ACCESS_DENIED: 'No tienes acceso a esta tienda.',
  PURCHASE_LINES_REQUIRED: 'Agrega al menos un insumo antes de confirmar la compra.',
  PURCHASE_NOT_CONFIRMED: 'Solo una compra confirmada puede registrarse en stock.',
  PURCHASE_STOCK_ALREADY_POSTED: 'La compra ya actualizó stock y no puede cancelarse.',
  PURCHASE_STOCK_SCOPE_INVALID: 'La compra contiene datos fuera del negocio actual.',
  PURCHASE_STOCK_UNIT_MISMATCH: 'Una o más líneas usan una unidad que no es compatible con la unidad base de su insumo.',
  PURCHASE_NOT_EDITABLE: 'Solo se pueden eliminar o editar compras en borrador.',
  PURCHASE_CATALOG_PENDING: 'No se puede aprobar: esta compra usa un proveedor o insumo que no está aprobado (está pendiente o fue rechazado). Regresa la compra a borrador y cambia el insumo, o resuelve primero el catálogo en "Aprobaciones de catálogo".',
  PURCHASE_NOT_FOUND: 'La compra no existe en este negocio.',
  PURCHASE_VOID_CONFIRM_REQUIRED: 'Confirma la anulación para continuar.',
  PURCHASE_VOID_REASON_REQUIRED: 'El motivo de la anulación debe tener al menos 10 caracteres.',
  PURCHASE_VOID_NOT_CONFIRMED: 'Solo se puede anular una compra confirmada. Una compra en borrador se elimina.',
  PURCHASE_VOID_INSUFFICIENT_STOCK: 'No se puede anular: el stock de esta compra ya se usó (venta, merma, ajuste o traspaso) y quedaría negativo.',
  // Fase A de auditoría (N360-26): códigos de Compras que caían al genérico de 409. Solo traducción;
  // los flujos de Compras no cambian.
  PURCHASE_EMPLOYEE_STORE_REQUIRED: 'Tu usuario necesita exactamente una tienda asignada para registrar compras. Pide a un dueño o gerente que lo revise.',
  PURCHASE_STATUS_INVALID: 'Esta compra no puede pasar a ese estado desde su estado actual. Recarga la lista e inténtalo de nuevo.',
  PURCHASE_REJECT_NOT_PENDING: 'Solo se resuelven compras enviadas a aprobación. Una compra en borrador se elimina y una confirmada se anula.',
  PURCHASE_REJECT_STOCK_POSTED: 'Esta compra ya afectó inventario; anúlala con la reversa de compra, no con el rechazo.',
  PURCHASE_TICKET_SYNC_LOCKED: 'Esta compra nació de un ticket: su tienda, fecha y método de pago se fijan en el ticket y no pueden cambiarse por separado.',
  INVENTORY_STOCK_PERMISSION_REQUIRED: 'No tienes permiso para consultar o registrar stock.',
  INVENTORY_UNIT_INCOMPATIBLE: 'La unidad elegida no es compatible con la unidad base del insumo.',
  INVENTORY_UNIT_SYMBOL_DUPLICATE: 'Ya existe una unidad con ese símbolo. Usa esa unidad en vez de crear una nueva.',
  INVENTORY_UNIT_BASE_DUPLICATE: 'Ya existe una unidad base para ese tipo. Solo puede haber una unidad base por tipo (peso, volumen o piezas).',
  INGREDIENT_BASE_UNIT_LOCKED: 'No se puede cambiar la unidad base de un insumo que ya tiene compras o movimientos de inventario registrados. Crea un insumo nuevo si necesitas otra unidad base.',
  INVENTORY_UNIT_SYSTEM_LOCKED: 'Esta es una unidad básica del sistema y no se puede editar. Crea una unidad nueva si necesitas otra definición.',
  STOCK_STORE_NOT_FOUND: 'Selecciona una tienda válida.',
  STOCK_STORE_ACCESS_DENIED: 'No tienes acceso a esta tienda.',
  STOCK_STORE_REQUIRED: 'Selecciona una tienda para consultar el inventario.',
  CONSUMPTION_STORE_REQUIRED: 'Selecciona una tienda para calcular la simulación.',
  CONSUMPTION_STORE_ACCESS_DENIED: 'No tienes acceso a esta tienda.',
  TRANSFER_STORE_REQUIRED: 'Selecciona una tienda de origen o destino para consultar traspasos.',
  STOCK_INGREDIENT_NOT_FOUND: 'Selecciona un insumo válido.',
  STOCK_UNIT_NOT_FOUND: 'Selecciona una unidad válida.',
  STOCK_QUANTITY_INVALID: 'La cantidad debe ser mayor a cero.',
  STOCK_WASTE_REASON_REQUIRED: 'La merma requiere una razón o nota.',
  STOCK_INSUFFICIENT: 'Stock insuficiente para registrar esta salida.',
  STOCK_MOVEMENT_SCOPE_INVALID: 'Los datos del movimiento no pertenecen al negocio actual.',
  // F1 · Mermas (inventory-waste-employee).
  WASTE_PERMISSION_REQUIRED: 'No tienes permiso para registrar mermas.',
  WASTE_REVIEW_PERMISSION_REQUIRED: 'Solo un dueño o gerente puede revisar las mermas del equipo.',
  WASTE_REVERSE_PERMISSION_REQUIRED: 'Solo un dueño o gerente puede anular una merma.',
  WASTE_THRESHOLD_PERMISSION_REQUIRED: 'Solo el dueño puede cambiar cuándo una merma se considera grande.',
  WASTE_THRESHOLD_INVALID: 'Escribe un número mayor a 0: hasta $1,000,000 en pesos o hasta 100 en porcentaje.',
  WASTE_STORE_NOT_ASSIGNED: 'No tienes una tienda asignada. Pide a tu dueño o gerente que te asigne una.',
  WASTE_STORE_REQUIRED: 'Elige en qué tienda pasó la merma.',
  WASTE_STORE_ACCESS_DENIED: 'No puedes registrar ni revisar mermas de esa tienda.',
  WASTE_CATEGORY_INVALID: 'Elige por qué se perdió el producto.',
  WASTE_CATEGORY_RESERVED: 'Esa categoría solo se usa al confirmar un conteo físico.',
  WASTE_NOTE_REQUIRED: 'Cuando eliges «Otro», escribe una nota que explique qué pasó.',
  WASTE_INGREDIENT_NOT_FOUND: 'Elige un insumo de la lista.',
  WASTE_STOCK_INSUFFICIENT: 'El sistema no tiene suficiente existencia de este insumo en esta tienda para registrar esa merma. Avísale a tu gerente.',
  WASTE_REQUEST_REUSED: 'Esta captura ya se había registrado con otros datos (quizá se guardó aunque no viste la confirmación). Revisa las mermas de hoy antes de registrar otra.',
  WASTE_SCOPE_INVALID: 'Este insumo o esta tienda cambió. Vuelve a elegirlo.',
  WASTE_UNIT_INVALID: 'Este insumo cambió de unidad. Vuelve a elegirlo.',
  STOCK_REFERENCE_RESERVED: 'Esa referencia está reservada por el sistema. Usa otra.',
  INGREDIENT_NOT_APPROVED: 'Ese insumo está pendiente de aprobación; todavía no se puede usar.',
  // P-078 (BK09/BK10): dos 409 de Aprobaciones que salían con el mensaje genérico de conflicto.
  CATALOG_MERGED: 'Esta propuesta ya se unió a un registro existente y no se puede reconsiderar: usa ese registro.',
  CATALOG_REJECT_HAS_OPEN_PURCHASES: 'No se puede rechazar: hay compras abiertas que lo usan. Apruébalo, únelo a uno existente o resuelve esas compras desde Compras.',
  WASTE_NOT_FOUND: 'La merma no existe.',
  WASTE_NOT_REVERSIBLE: 'Solo se puede anular una merma.',
  WASTE_ALREADY_REVERSED: 'Esta merma ya estaba anulada. No se devolvió nada otra vez.',
  WASTE_NOT_RESTORABLE: 'Esta merma no se puede anular aquí: el insumo cambió de unidad o ya no tiene existencia registrada en esta tienda. Corrígelo con un ajuste en Inventario.',
  WASTE_REVERSAL_REASON_REQUIRED: 'Escribe por qué se anula (al menos 10 caracteres).',
  WASTE_NOT_REVIEWABLE: 'Esta merma no está pendiente de revisión (no está marcada, ya se anuló o se reconoció al contar).',
  VOID_REQUEST_APPLY_FAILED: 'No fue posible aplicar la anulación en este momento. La solicitud sigue pendiente: intenta aprobarla de nuevo.',
  WASTE_RANGE_INVALID: 'La fecha inicial no puede ser posterior a la final.',
  WASTE_RANGE_TOO_LONG: 'Elige un rango de hasta 3 meses.',
  WASTE_COUNTED_AFTER: 'Después de esta merma se contó el insumo en esta tienda: el conteo ya la tomó en cuenta y anularla dejaría más de lo que hay en el estante. Si el conteo estuvo mal, vuelve a contar.',
  WASTE_FROM_COUNT: 'Esta merma se reconoció al contar y no se anula aquí: devolverla dejaría más existencia de la que se contó. Si el conteo estuvo mal, vuelve a contar el insumo en Inventario → Stock.',
  STOCK_MOVEMENT_UNIT_MISMATCH: 'La unidad seleccionada no es compatible con la unidad base de este insumo.',
  TRANSFER_STORES_EQUAL: 'La tienda origen y destino no pueden ser iguales.',
  TRANSFER_LINES_REQUIRED: 'Agrega al menos un insumo al traspaso.',
  TRANSFER_INSUFFICIENT_STOCK: 'Stock insuficiente en tienda origen.',
  TRANSFER_NOT_EDITABLE: 'Solo puedes editar un traspaso en borrador.',
  TRANSFER_CONFIRMED_NOT_CANCELLABLE: 'Un traspaso confirmado ya no puede cancelarse.',
  TRANSFER_LINE_DUPLICATE: 'Ese insumo ya está incluido en el traspaso.',
  TRANSFER_LINE_UNIT_MISMATCH: 'La unidad seleccionada no es compatible con la unidad base de este insumo.',
  INVENTORY_CONSUMPTION_PERMISSION_REQUIRED: 'No tienes permiso para consultar la simulación de consumo.',
  SALES_POSTING_CONFIRM_REQUIRED: 'Debes confirmar la acción para descontar inventario.',
  SALES_POSTING_STORE_REQUIRED: 'Selecciona una tienda para descontar inventario por ventas.',
  SALES_POSTING_STORE_ACCESS_DENIED: 'No tienes acceso a esta tienda.',
  SALES_POSTING_NOT_READY: 'Todavía hay problemas antes de descontar inventario. Corrígelos en la simulación antes de continuar.',
  SALES_POSTING_BLOCKED: 'No fue posible completar el descuento; el stock pudo haber cambiado. Vuelve a calcular la simulación e inténtalo de nuevo.',
  SALES_POSTING_PERMISSION_REQUIRED: 'No tienes permiso para descontar inventario por ventas.',
  SALES_POSTING_NOT_FOUND: 'La corrida de descuento no existe en este negocio.',
  SALES_REVERSAL_CONFIRM_REQUIRED: 'Debes confirmar la acción para revertir el descuento.',
  SALES_REVERSAL_CONFIRMATION_TEXT_INVALID: 'Escribe "REVERSAR" para confirmar la reversa.',
  SALES_REVERSAL_REASON_REQUIRED: 'El motivo de la reversa debe tener al menos 10 caracteres.',
  SALES_REVERSAL_PERMISSION_REQUIRED: 'No tienes permiso para revertir descuentos de inventario.',
  SALES_REVERSAL_STORE_ACCESS_DENIED: 'No tienes acceso a esta tienda.',
  SALES_REVERSAL_NOT_REVERSIBLE: 'Solo se puede revertir una corrida de descuento aplicada.',
  SALES_REVERSAL_BALANCE_MISSING: 'No fue posible devolver el stock porque la existencia ya no está registrada.',
  COSTING_DEFAULT_INACTIVE: 'No puedes usar una presentación inactiva como referencia de costo.',
  SALES_CONSUMPTION_RANGE_INVALID: 'El rango de fechas no es válido.',
  SALES_CONSUMPTION_RANGE_TOO_LARGE: 'El rango máximo permitido es de 90 días.',
  LOYVERSE_SALES_RANGE_INVALID: 'El rango de fechas no es válido.',
  LOYVERSE_SALES_RANGE_TOO_LARGE: 'El rango máximo permitido es de 90 días.',
  RECIPE_COVERAGE_RANGE_INVALID: 'El rango de fechas no es válido.',
  RECIPE_COVERAGE_RANGE_TOO_LARGE: 'El rango máximo permitido es de 90 días.',
  SALES_COST_RANGE_INVALID: 'El rango de fechas no es válido.',
  SALES_COST_RANGE_TOO_LARGE: 'El rango máximo permitido es de 90 días.',
  USER_UPDATE_CONFLICT: 'El usuario cambió durante la operación. Recarga e intenta nuevamente.',
  INTEGRATION_PERMISSION_REQUIRED: 'No tienes permiso para consultar integraciones.',
  LOYVERSE_NOT_CONFIGURED: 'Configura un token Loyverse activo antes de probar la conexión.',
  LOYVERSE_TOKEN_INVALID: 'Token inválido o sin permisos.',
  LOYVERSE_RATE_LIMITED: 'Loyverse limitó temporalmente las solicitudes. Intenta más tarde.',
  LOYVERSE_TIMEOUT: 'La conexión con Loyverse excedió el tiempo de espera.',
  LOYVERSE_UNAVAILABLE: 'No fue posible conectar con Loyverse.',
  LOYVERSE_REQUEST_FAILED: 'Loyverse no pudo completar la prueba de conexión.',
  LOYVERSE_ENCRYPTION_ERROR: 'La configuración segura de Loyverse no está disponible en el backend.',
  LOYVERSE_PAGINATION_INVALID: 'Loyverse devolvió una paginación inválida.',
  LOYVERSE_PAGINATION_LIMIT: 'La sincronización excedió el límite seguro de páginas.',
  LOYVERSE_SYNC_FAILED: 'No fue posible completar la sincronización Loyverse.',
  LOYVERSE_RECEIPT_RANGE_INVALID: 'El rango de fechas no es válido.',
  LOYVERSE_RECEIPT_RANGE_TOO_LARGE: 'El rango máximo permitido es de 7 días.',
  LOYVERSE_RECEIPT_SYNC_IN_PROGRESS: 'Ya hay una actualización de ventas en proceso.',
  LOYVERSE_RECEIPT_SYNC_FAILED: 'No fue posible actualizar las ventas.',
  RECEIPT_PERMISSION_REQUIRED: 'No tienes permiso para consultar receipts.',
  RECEIPT_STORE_REQUIRED: 'Selecciona una tienda para consultar ventas.',
  RECEIPT_STORE_ACCESS_DENIED: 'No tienes acceso a esta tienda.',
  SHIFT_CLOSE_PERMISSION_REQUIRED: 'No tienes permiso para consultar cortes de turno.',
  SHIFT_CLOSE_STORE_REQUIRED: 'Selecciona una tienda para consultar cortes de turno.',
  SHIFT_CLOSE_VOID_PERMISSION_REQUIRED: 'No tienes permiso para anular cortes de turno.',
  SHIFT_STORE_NOT_FOUND: 'La tienda no pertenece a este negocio.',
  SHIFT_STORE_ACCESS_DENIED: 'No tienes acceso a esta tienda.',
  SHIFT_RANGE_INVALID: 'La hora de fin debe ser posterior a la hora de inicio.',
  SHIFT_RANGE_TOO_LARGE: 'Un corte de turno no puede abarcar más de 24 horas.',
  SHIFT_CLOSE_CONFIRM_REQUIRED: 'Debes confirmar la acción para guardar el corte.',
  SHIFT_CLOSE_DUPLICATE: 'Ya existe un corte guardado para esta tienda y este rango.',
  SHIFT_CLOSE_OVERLAP: 'Ya existe un corte cerrado que se traslapa con este rango. Revisa el historial antes de guardar.',
  SHIFT_CLOSE_NOT_FOUND: 'El corte de turno no existe en este negocio.',
  SHIFT_CLOSE_VOID_CONFIRM_REQUIRED: 'Debes confirmar la acción para anular el corte.',
  SHIFT_CLOSE_VOID_CONFIRMATION_TEXT_INVALID: 'Escribe "ANULAR" para confirmar la anulación.',
  SHIFT_CLOSE_VOID_REASON_REQUIRED: 'El motivo de la anulación debe tener al menos 10 caracteres.',
  SHIFT_CLOSE_ALREADY_VOIDED: 'Este corte ya fue anulado anteriormente.',
  SHIFT_CLOSE_NOT_VOIDABLE: 'Solo se puede anular un corte cerrado.',
  DASHBOARD_PERMISSION_REQUIRED: 'No tienes permiso para ver este dashboard.',
  FINANCIALS_PERMISSION_REQUIRED: 'No tienes permiso para ver información financiera.',
  DASHBOARD_RANGE_INVALID: 'Revisa las fechas seleccionadas.',
  DASHBOARD_RANGE_TOO_LARGE: 'El rango seleccionado es demasiado amplio. Elige un periodo más corto.',
  DASHBOARD_STORE_NOT_FOUND: 'La tienda seleccionada no está disponible para este negocio.',
  EXPENSE_PERMISSION_REQUIRED: 'No tienes permiso para administrar Gastos.',
  EXPENSE_CONFIRM_PERMISSION_REQUIRED: 'No tienes permiso para confirmar gastos.',
  EXPENSE_VOID_PERMISSION_REQUIRED: 'No tienes permiso para anular gastos.',
  EXPENSE_STORE_NOT_FOUND: 'La tienda no pertenece a este negocio.',
  EXPENSE_STORE_ACCESS_DENIED: 'No tienes acceso a esta tienda.',
  EXPENSE_LIST_STORE_REQUIRED: 'Selecciona una tienda para consultar Gastos.',
  EXPENSE_NOT_FOUND: 'El gasto no existe en este negocio.',
  EXPENSE_NOT_EDITABLE: 'Los gastos confirmados no se eliminan. Deben anularse con motivo.',
  EXPENSE_NOT_CONFIRMABLE: 'Solo un gasto en borrador puede confirmarse.',
  EXPENSE_ALREADY_CONFIRMED: 'Este gasto ya estaba confirmado.',
  EXPENSE_CANCEL_CONFIRM_REQUIRED: 'Confirma la anulación para continuar.',
  EXPENSE_CANCEL_REASON_REQUIRED: 'El motivo de la anulación debe tener al menos 10 caracteres.',
  EXPENSE_CANCEL_NOT_CONFIRMED: 'Solo se puede anular un gasto confirmado. Un gasto en borrador se elimina.',
  EXPENSE_TAX_PERCENTAGE_REQUIRED: 'Especifica el porcentaje de IVA.',
  EXPENSE_SUBCATEGORY_DUPLICATE: 'Ya existe una subcategoría con ese nombre en esta categoría.',
  OTHER_SALE_PERMISSION_REQUIRED: 'No tienes permiso para administrar Ventas e ingresos.',
  OTHER_SALE_CONFIRM_PERMISSION_REQUIRED: 'No tienes permiso para confirmar ventas.',
  OTHER_SALE_VOID_PERMISSION_REQUIRED: 'No tienes permiso para anular ventas.',
  OTHER_SALE_STORE_NOT_FOUND: 'La tienda no pertenece a este negocio.',
  OTHER_SALE_STORE_ACCESS_DENIED: 'No tienes acceso a esta tienda.',
  OTHER_SALE_LIST_STORE_REQUIRED: 'Selecciona una tienda para consultar Ventas e ingresos.',
  OTHER_SALE_NOT_FOUND: 'La venta no existe en este negocio.',
  OTHER_SALE_NOT_EDITABLE: 'Las ventas confirmadas no se eliminan. Deben anularse con motivo.',
  OTHER_SALE_NOT_CONFIRMABLE: 'Solo una venta en borrador puede confirmarse.',
  OTHER_SALE_ALREADY_CONFIRMED: 'Esta venta ya estaba confirmada.',
  OTHER_SALE_CANCEL_CONFIRM_REQUIRED: 'Confirma la anulación para continuar.',
  OTHER_SALE_CANCEL_REASON_REQUIRED: 'El motivo de la anulación debe tener al menos 10 caracteres.',
  OTHER_SALE_CANCEL_NOT_CONFIRMED: 'Solo se puede anular una venta confirmada. Una venta en borrador se elimina.',
  OTHER_SALE_TAX_PERCENTAGE_REQUIRED: 'Especifica el porcentaje de IVA.',
  OTHER_SALE_SUBCATEGORY_DUPLICATE: 'Ya existe una subcategoría con ese nombre en esta categoría.',
  OTHER_SALE_SUBCATEGORY_NOT_FOUND: 'La subcategoría no pertenece a este negocio.',
  OTHER_SALE_SUBCATEGORY_MISMATCH: 'La subcategoría no pertenece a la categoría seleccionada.',
  OTHER_SALE_CHECKOUT_IDEMPOTENCY_CONFLICT: 'Esta venta cambió desde el último intento. Inicia una venta nueva.',
  OTHER_SALE_CHECKOUT_DUPLICATE_PRODUCT: 'Cada producto puede aparecer solo una vez en el pedido.',
  OTHER_SALE_CHECKOUT_PRODUCT_NOT_FOUND: 'Uno o más productos ya no existen. Actualiza el catálogo.',
  OTHER_SALE_CHECKOUT_PRODUCT_INACTIVE: 'Un producto del pedido quedó inactivo. Quítalo para continuar.',
  OTHER_SALE_CHECKOUT_PRODUCT_NOT_IN_STORE: 'Un producto del pedido no está disponible en esta tienda.',
  OTHER_SALE_CHECKOUT_STORE_REQUIRED: 'Selecciona la tienda donde se realiza la venta.',
  OTHER_SALE_EMPLOYEE_STORE_REQUIRED: 'Tu usuario necesita exactamente una tienda asignada para registrar ventas. Pide a un dueño o gerente que lo revise.',
  // Fase A de auditoría (N360-26) + revisión independiente H-1 (2026-09-16): POS nativo y cobro con
  // lector (Gate-02). Doctrina del proyecto: UNKNOWN no es FAILED. Cada texto refleja lo que el backend
  // SABE en el sitio que lanza el código (clasificación en docs/PHASE_AUDIT_CRITICAL_FIXES_A.md §4 y en
  // client.test.ts): cuando el resultado es INCIERTO —una observación del lector rechazada, un intento
  // en vilo, o algo que impide verificar— el mensaje dice que Negocio360 todavía no puede confirmar el
  // resultado, prohíbe volver a cobrar y nunca afirma «no se cobró» ni «no se aplicó ningún cambio».
  // PAYMENT_CLOSED solo lo produce el dominio cuando la venta ya se cobró por otro método, así que
  // tampoco invita a un cobro nuevo.
  NATIVE_SALE_NOT_FOUND: 'La venta no existe en este negocio.',
  NATIVE_SALE_NOT_PAYABLE: 'Esta venta ya no está pendiente de cobro. Revisa su estado antes de continuar.',
  NATIVE_SALE_ALREADY_CANCELED: 'Esta venta se canceló antes de cobrarse. Inicia una venta nueva.',
  NATIVE_SALE_ALREADY_COMPLETED: 'Esta venta ya se cobró. Para deshacerla hay que anularla desde el historial de ventas.',
  NATIVE_SALE_STORE_REQUIRED: 'La venta no tiene tienda y no puede completarse. Avísale a tu gerente.',
  NATIVE_SALE_PAYMENT_METHOD_INVALID: 'Ese método de cobro no está disponible.',
  NATIVE_SALE_PAYMENT_AMOUNT_MISMATCH: 'El cobro debe ser por el total de la venta.',
  NATIVE_SALE_PAYMENT_KEY_REUSED: 'Esta clave de cobro ya se usó en otra venta, así que Negocio360 no puede aplicarla a esta. Revisa el historial de ventas antes de continuar y no vuelvas a cobrar.',
  CLIENT_MEDIATED_PAYMENTS_DISABLED: 'Los cobros con lector no están habilitados en este servidor.',
  CLIENT_MEDIATED_LIVE_REQUIRES_RECONCILIATION: 'El lector reportó este cobro como completado, pero Negocio360 todavía no puede confirmarlo con el procesador: la tarjeta pudo haberse cobrado. No vuelvas a cobrar. Avísale a tu dueño o gerente para que lo verifiquen antes de hacer otra cosa con la venta.',
  PAYMENT_PROVIDER_NOT_AVAILABLE: 'Este negocio no tiene un procesador de cobros con lector configurado.',
  PAYMENT_ATTEMPT_NOT_FOUND: 'El intento de cobro no existe en este negocio.',
  PAYMENT_ATTEMPT_NOT_CLIENT_MEDIATED: 'Este cobro no se ejecuta desde el lector de este dispositivo.',
  PAYMENT_ATTEMPT_ALREADY_RESOLVED: 'Este cobro ya tiene un resultado registrado. Revisa la venta antes de hacer otra cosa; no vuelvas a cobrar.',
  PAYMENT_ATTEMPT_NOT_QUERYABLE: 'Negocio360 no tiene el identificador del procesador para este cobro, así que todavía no puede consultarlo ni confirmar su resultado. No vuelvas a cobrar. Espera un momento, usa «Verificar cobro» y, si sigue igual, avísale a tu gerente.',
  PAYMENT_ATTEMPT_IN_FLIGHT: 'Esta venta ya tiene un cobro con lector cuyo resultado Negocio360 todavía no puede confirmar. No vuelvas a cobrar: espera a que se resuelva o usa «Verificar cobro».',
  PAYMENT_ALREADY_SUCCEEDED: 'Esta venta ya tiene un cobro confirmado.',
  PAYMENT_CLOSED: 'Este cobro con lector ya quedó cerrado: la venta se cobró por otro método y ningún intento con el lector se confirmó. Revisa la venta en el historial; no vuelvas a cobrar.',
  SALE_HAS_PAYMENT_IN_FLIGHT: 'Negocio360 todavía no puede confirmar si el cobro con lector de esta venta se realizó, así que la venta no se puede cancelar ni cobrar por otro método. No vuelvas a cobrar; usa «Verificar cobro».',
  SALE_HAS_SUCCEEDED_PAYMENT: 'Esta venta ya tiene un cobro confirmado: no se puede cancelar ni cobrar por otro método.',
  // FI-53: completar una venta ya cobrada exige que el servidor tenga su cobro confirmado.
  SALE_HAS_NO_CONFIRMED_PAYMENT: 'El servidor no tiene un cobro confirmado para esta venta, así que no la puede completar. Vuelve a abrirla desde las ventas guardadas para ver cómo quedó.',
  OBSERVATION_AMOUNT_MISMATCH: 'El importe que reportó el lector no coincide con el de la venta, así que Negocio360 no puede confirmar el resultado de este cobro. No vuelvas a cobrar: la tarjeta pudo haberse cobrado. Usa «Verificar cobro» o avísale a tu gerente.',
  OBSERVATION_CURRENCY_MISMATCH: 'La moneda que reportó el lector no coincide con la de la venta, así que Negocio360 no puede confirmar el resultado de este cobro. No vuelvas a cobrar: la tarjeta pudo haberse cobrado. Usa «Verificar cobro» o avísale a tu gerente.',
  OBSERVATION_EVIDENCE_REQUIRED: 'El lector reportó el cobro como completado, pero sin la evidencia completa (transacción, importe y moneda), así que Negocio360 no puede confirmar su resultado. No vuelvas a cobrar: la tarjeta pudo haberse cobrado. Usa «Verificar cobro» o avísale a tu gerente.',
  PROVIDER_ATTEMPT_ID_CONFLICT: 'La transacción de este cobro ya está registrada en otro cobro de Negocio360, así que no se puede confirmar ni descartar. No vuelvas a cobrar: requiere revisión de tu dueño o gerente.',
  MERCHANT_ACCOUNT_NOT_FOUND: 'No hay una cuenta de cobro configurada en este negocio.',
  MERCHANT_ACCOUNT_DISABLED: 'La cuenta de cobro está deshabilitada. Pide al Dueño que la revise.',
  PAYMENT_NOT_FOUND: 'El cobro no existe en este negocio.',
  PAYMENT_NOT_INTEGRATED: 'Este cobro se registró por fuera; Negocio360 no lo procesa ni lo puede verificar.',
  PAYMENT_AMOUNT_NOT_REPRESENTABLE: 'El importe de la venta no se puede cobrar con lector. Revisa los precios o cobra con otro método.',
  PAYMENT_PROVIDER_NOT_RECONCILABLE: 'Negocio360 no puede verificar este cobro con este procesador, así que su resultado sigue sin confirmarse. No vuelvas a cobrar; avísale a tu dueño o gerente.',
  PAYMENT_RECONCILIATION_NOT_DUE: 'Otra corrida de verificación ya tomó este cobro. No vuelvas a cobrar; consulta el resultado en un momento.',
  // PayPal/Zettle LIVE readiness: conexión con Zettle y cobro con dinero real.
  PAYMENT_RECONCILIATION_DISABLED: 'La verificación con el procesador no está habilitada en este servidor. Si estabas verificando un cobro, no vuelvas a cobrar.',
  PAYMENT_OAUTH_OWNER_REQUIRED: 'Solo el Dueño puede administrar la cuenta de Zettle.',
  PAYMENT_OAUTH_STATE_INVALID: 'La autorización no corresponde a esta sesión, ya se usó o expiró. Vuelve a conectar.',
  PAYMENT_OAUTH_EXCHANGE_FAILED: 'Zettle no aceptó la autorización. Vuelve a conectar.',
  PAYMENT_OAUTH_IDENTITY_FAILED: 'No se pudo identificar tu cuenta en Zettle. Intenta de nuevo.',
  PAYMENT_OAUTH_IDENTITY_INVALID: 'Zettle devolvió una identidad de cuenta que no es válida.',
  PAYMENT_ACCOUNT_DEVELOPER_MODE_ACTIVE: 'Este negocio tiene activa la cuenta de pruebas (Developer Mode). Desactívala antes de conectar una cuenta real.',
  PAYMENT_ACCOUNT_ORGANIZATION_MISMATCH: 'Este negocio ya tiene conectada otra cuenta de Zettle. Desconéctala primero.',
  PAYMENT_ACCOUNT_ORGANIZATION_IN_USE: 'Esta cuenta de Zettle ya está conectada a otro negocio.',
  PAYMENT_ACCOUNT_CONFLICT: 'Otra conexión se completó al mismo tiempo. Recarga la página.',
  PAYMENT_ACCOUNT_NOT_FOUND: 'No hay una cuenta de Zettle conectada.',
  PAYMENT_ACCOUNT_AMBIGUOUS: 'Este negocio tiene más de una cuenta de cobro con lector activa.',
  PAYMENT_DISCONNECT_CONFIRM_REQUIRED: 'Escribe "DESCONECTAR" para confirmar.',
  PAYMENT_LIVE_NOT_ENABLED: 'Esta cuenta de cobro todavía no está habilitada para cobrar con dinero real.',
  PAYMENT_RECONCILE_RATE_LIMITED: 'Demasiadas verificaciones seguidas. No vuelvas a cobrar; espera un momento antes de verificar de nuevo.',
  PAYMENT_RECONCILE_TOO_SOON: 'Este cobro se acaba de verificar y su resultado todavía puede estar pendiente. No vuelvas a cobrar; espera unos segundos antes de verificar de nuevo.',
  PAYMENT_RECONCILIATION_IN_PROGRESS: 'Negocio360 ya está verificando este cobro con el procesador. No vuelvas a cobrar; consulta el resultado en un momento.',
  // Fase A de auditoría (N360-26): candado LIVE por cuenta (administración del Dueño).
  PAYMENT_LIVE_ACCOUNT_INACTIVE: 'La cuenta de cobro no está activa. Pide al Dueño que la revise.',
  PAYMENT_LIVE_CREDENTIAL_REQUIRED: 'La cuenta no tiene una conexión vigente con el procesador. El Dueño debe conectarla primero.',
  PAYMENT_LIVE_DEVELOPER_MODE: 'La cuenta de pruebas (Developer Mode) no puede habilitarse para cobrar con dinero real.',
  PAYMENT_LIVE_PROVIDER_UNSUPPORTED: 'Este procesador todavía no admite cobros con dinero real.',
  PAYMENT_LIVE_REASON_REQUIRED: 'Escribe un motivo más completo para este cambio.',
};

function getStatusMessage(status: number): string {
  if (status === 400) return 'La información enviada no es válida.';
  if (status === 401) return 'Tu sesión no es válida o expiró.';
  if (status === 403) return 'No tienes permiso para esta acción.';
  if (status === 404) return 'El recurso solicitado no existe.';
  if (status === 409) return 'La operación entra en conflicto con información existente.';
  if (status === 429) return 'Demasiadas solicitudes. Espera un momento e intenta nuevamente.';
  return 'El servidor no pudo completar la solicitud.';
}
